import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import useAppContext from "../../context/hooks/useAppContext";
import useFleetManagerContext from "../../context/hooks/useFleetManagerContext";
import { getRegistrationIdByParkingRightId } from "../../helpers/parkingRight.utils";
import { RevokeParkingRightsModel } from "../../models/RevokeParkingRightsModel";
import { useAppDispatch, useAppSelector } from "../../store";
import parkingProductsActions from "../../store/actions/parkingProducts.actions";
import * as entryRightsService from "../../services/entryRights.service";
import Warning from "./Warning";
import { FormControlLabel } from "@material-ui/core";
import {
    isEntryRightsMyProductsRowKey,
    selectDefaultRevocationEmailValue,
    selectEnableDelegationsScheduling,
} from "../../containers/FleetManager/MyProducts/ParkingRightsDataTable/common";
import { RevokeParkingRight } from "../../models/RevokeParkingRight";
import { DelegeeRevokeModel } from "../../models/vehicle-assignments/RevokeEntryRightsModel";
import { groupBy } from "underscore";
import { EntryProduct } from "../../models/delegations/DelegatedEntryRight";

interface IFormProps {
    selectedParkingRightIds: string[];
    onRefresh: () => void;
}

const RevokeParkingRightsWarning = ({ selectedParkingRightIds, onRefresh }: IFormProps) => {
    const { t } = useTranslation(["revokeDelegeeWarning", "globals"]);
    const { toggleRevokeParkingRightsWarning, fleetManagerState } = useFleetManagerContext();
    const { appState } = useAppContext();
    const dispatch = useAppDispatch();
    const enableDelegationsScheduling = useAppSelector(selectEnableDelegationsScheduling);
    const loading = useAppSelector((state) => state.parkingProducts.loading);
    const delegatedParkingProducts = useAppSelector(
        (state) => state.parkingProducts.delegatedParkingProducts
    );
    const delegees = useAppSelector((state) => state.parkingProducts.delegees);

    const defaultEmailValue = useAppSelector(selectDefaultRevocationEmailValue);
    const [sendRevocationEmail, setSendRevocationEmail] = useState(defaultEmailValue);

    const revokeCount = enableDelegationsScheduling ? 1 : selectedParkingRightIds.length;
    const warningText = t("warning", { count: revokeCount });
    const warningDetails = t("details", { count: revokeCount });

    const handleRevokeCancellation = () => {
        toggleRevokeParkingRightsWarning();
    };

    const handleSuccessfulRevoke = () => {
        toggleRevokeParkingRightsWarning();
        onRefresh();
    };

    const handleRevokeConfirmation = () => {
        const { selectedDelegeeRowKey } = fleetManagerState;
        if (enableDelegationsScheduling && isEntryRightsMyProductsRowKey(selectedDelegeeRowKey)) {
            const { delegeeId, entryRightId, pmcId } = selectedDelegeeRowKey;

            if (!entryRightId || !pmcId) {
                return;
            }

            const delegeeRevokeModels: DelegeeRevokeModel[] = [
                {
                    delegeeId: delegeeId,
                    products: [{ entryRightId, pmcId }],
                },
            ];

            dispatch(
                entryRightsService.revokeEntryRights(
                    {
                        customerId: appState.user.seasonTicketOwnerCrmId!,
                        delegeeRevokeModels: delegeeRevokeModels,
                        sendRevocationConfirmationEmail: sendRevocationEmail,
                    },
                    "parking",
                    handleSuccessfulRevoke
                )
            );
            return;
        }
        const parkingRights: RevokeParkingRight[] = selectedParkingRightIds.map((id) =>
            getRegistrationIdByParkingRightId(id, delegatedParkingProducts, delegees)
        );

        const model: RevokeParkingRightsModel = {
            parkingRights,
            seasonTicketOwnerCrmId: appState.user.seasonTicketOwnerCrmId as string,
            sendRevocationConfirmationEmail: sendRevocationEmail,
        };
        dispatch(parkingProductsActions.revokeParkingRights(model, handleSuccessfulRevoke));
    };

    return (
        <Warning
            warning={warningText}
            details={warningDetails}
            onCancel={handleRevokeCancellation}
            onConfirm={handleRevokeConfirmation}
            disabled={false}
            confirmText={t("globals:revoke")}
            loading={loading}
        >
            <div>
                <FormControlLabel
                    control={
                        <qp-switch
                            data-size="s"
                            data-checked={sendRevocationEmail}
                            onClick={() => setSendRevocationEmail(!sendRevocationEmail)}
                        ></qp-switch>
                    }
                    label={t("revokeDelegeeWarning:sendRevocationEmail")}
                />
            </div>
        </Warning>
    );
};

export default RevokeParkingRightsWarning;
